import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import {
  ActivityFileTypeEnum,
  ActivityStatusEnum,
  SortEnum,
  TimeslotStatusEnum,
  TimeslotWorkflowActionEnum,
} from '@sport-activities/sdk';
import { forkJoin, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { ActivityCardVariantEnum } from '~/component/activity-card/enum/variant.enum';
import { TimeslotListVariantEnum } from '~/component/activity-timeslot-list/enums/timeslot-list-variant.enum';
import { BadgeVariantEnum } from '~/component/badge/enums/variant.enum';
import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { CtaWidthEnum } from '~/component/call-to-action/enums/width.enum';
import { DividerVariantEnum } from '~/component/divider/enums/divider-variant.enum';
import { AlertModalComponent } from '~/component/modal/alert/alert.component';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { SocialButtonVariantEnum } from '~/component/social-button/enums/variant.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { ActivityDuplicationService } from '~/core/services/activity/activity-duplication.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { TitleService } from '~/core/services/title/title.service';
import { OrganizerActivityStatusEnum } from '~/organizer/enums/activity-status.enum';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { checkActivityMandatoryFields } from '~/organizer/shared/helpers/activity/checkActivityMandatoryFields/check-activity-mandatory-fields.helper';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { activityMandatoryFieldsDefaultValue } from '~/toggles/default-values/organizer/activity/by-type-mandatory-fields';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { environment } from '~environments/environment';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';

@Component({
  selector: 'app-organizer-activity-manage',
  templateUrl: './manage.component.html',
  styleUrls: ['./manage.component.scss'],
})
export class OrganizerActivityPageManageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(AlertModalComponent)
  public confirmActivityCancellationModal: AlertModalComponent;
  @ViewChild('shareModal')
  public shareModal: ModalComponent;
  @ViewChild('haveIncompleteFieldsModal')
  public haveIncompleteFieldsModal: ModalComponent;

  private readonly frontBaseUrl = environment.applications.front.url;
  private readonly subscriptions = new Subscription();

  public readonly activityCardVariant = ActivityCardVariantEnum;
  public readonly activityManagementCancelToggle =
    ToggleNameEnum.ActivityManagementCancel;
  public readonly activityManagementDuplicationToggle =
    ToggleNameEnum.ActivityManagementDuplication;
  public readonly ctaVariant = CtaVariantEnum;
  public readonly ctaWidth = CtaWidthEnum;
  public readonly dividerVariant = DividerVariantEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly icon = VtmnPlayIconTypeEnum;
  public readonly modalVariant = ModalVariantEnum;
  public readonly priceBadgeVariant = BadgeVariantEnum.YELLOW;
  public readonly socialButtonVariant = SocialButtonVariantEnum;
  public readonly timeslotVariant = TimeslotListVariantEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly toggleNameEnum = ToggleNameEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;

  public activity: Activity;
  public displayTimeslots = false;
  public duplicatedActivityId: number | string;
  public httpParams: Params = { preview: true };
  public isDraftActivity = false;
  public showDeletionSuccess = false;
  public showDuplicationSuccess = false;
  public showSeeMoreTimeslotsCta = true;
  public successStatus = SuccessStatusEnum.LOADING;
  public timeslots: Timeslot[];
  public isTabletDevice: boolean;

  constructor(
    @Inject('Window') private readonly window: Window,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly activityService: OrganizerSharedActivityService,
    private readonly redirectionService: RedirectionService,
    private readonly router: Router,
    private readonly titleService: TitleService,
    private readonly cdr: ChangeDetectorRef,
    private readonly matchMediaService: MatchMediaService,
    private readonly activityDuplicationService: ActivityDuplicationService,
    private readonly toggleService: ToggleService
  ) {}

  public ngAfterViewInit(): void {
    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        if (params.updated === 'true') {
          this.cdr.detectChanges();
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public get isActivityPreviewOpenInNewTab(): boolean {
    return this.toggleService.isEnabled(ToggleNameEnum.OrganizerPreviewNewTab);
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.organizer.activity.manage.title');

    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;
        this.isDraftActivity =
          this.activity.status === ('DRAFT' as ActivityStatusEnum);
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );

    this.activityService
      .getActivityTimeslots(
        this.activity?.sku?.toString(),
        {
          timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
          sort: {
            order: 'asc' as SortEnum,
            by: 'startDate',
          },
          startDate: new Date().toISOString(),
        },
        1,
        4
      )
      .subscribe((timeslots) => {
        this.timeslots = timeslots;
        this.displayTimeslots =
          !this.isDraftActivity && this.timeslots?.length > 0;
      });
  }

  public get activityUrl(): string {
    const path = getActivityDetailRouteName(
      this.activity?.type,
      this.activity?.sku
    );

    return (
      this.frontBaseUrl +
      this.localizeRouterService.translateRoute(path).toString()
    );
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute('/organizer.activity.list')
        .toString(),
    };
  }

  public get hasHeader(): boolean {
    return !this.isTabletDevice;
  }

  public get hasShareModal(): boolean {
    return !this.isTabletDevice || !this.window.navigator.share;
  }

  public get newDatesActionQueryParams(): object {
    if (
      this.toggleService.isEnabled(ToggleNameEnum.ActivityManagementPreSchedule)
    ) {
      return {};
    }

    return { activityId: this.activity.sku };
  }

  public get newDatesActionRouterLink(): string {
    return this.toggleService.isEnabled(
      ToggleNameEnum.ActivityManagementPreSchedule
    )
      ? `/organizer.timeslot.pre-schedule/${this.activity.sku}`
      : '/organizer.activity-planning.activity';
  }

  public get picture(): string {
    return (
      this.activity.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || this.activity.picture
    );
  }

  public get responsiveDividerVariant(): string {
    return this.isTabletDevice
      ? DividerVariantEnum.Large
      : DividerVariantEnum.Medium;
  }

  public closeConfirmActivityCancellationModal(): void {
    this.confirmActivityCancellationModal.hide();
  }

  public deleteActivity(): void {
    this.closeConfirmActivityCancellationModal();
    this.showDeletionSuccess = true;
    this.subscriptions.add(
      forkJoin([
        this.activityService.postActivityAction(this.activity.sku, 'archive'),
        this.activityService.updateTimeslotWorkflowBulk(
          this.activity.sku,
          this.timeslots.map(({ identifier }) => ({
            identifier,
            action: 'cancel' as TimeslotWorkflowActionEnum.CANCEL,
          }))
        ),
      ])
        .pipe(
          mergeMap(() =>
            forkJoin([
              this.activityService
                .getActivity(this.activity?.sku?.toString())
                .pipe(
                  repeatUntil(
                    (activity) =>
                      activity.status.toLowerCase() ===
                      OrganizerActivityStatusEnum.CANCELED
                  )
                ),
              this.activityService
                .getTimeslot(this.timeslots?.[0]?.identifier)
                .pipe(
                  repeatUntil(
                    (timeslot) =>
                      timeslot.status ===
                      ('CANCELED' as TimeslotStatusEnum.CANCELED)
                  )
                ),
            ])
          )
        )
        .subscribe(() => {
          this.successStatus = SuccessStatusEnum.SUCCESS;
        })
    );
  }

  public duplicateActivity(): void {
    this.showDuplicationSuccess = true;
    this.subscriptions.add(
      this.activityService
        .postActivity(this.activityDuplicationService.duplicate(this.activity))
        .pipe(
          mergeMap((activityId) => {
            return this.activityService
              .getActivity(activityId.toString())
              .pipe(
                repeatUntil(
                  (activity) =>
                    activity.status.toLowerCase() ===
                    OrganizerActivityStatusEnum.DRAFT
                )
              );
          })
        )
        .subscribe((activity) => {
          this.activityService.clearActivity();
          this.successStatus = SuccessStatusEnum.SUCCESS;
          this.duplicatedActivityId = activity.sku;
        })
    );
  }

  public editDuplicatedActivity(): void {
    this.router.navigate([this.getActivityEditLink(this.duplicatedActivityId)]);
  }

  public getActivityEditLink(id: number | string): string {
    return this.localizeRouterService
      .translateRoute(
        `/organizer.activity.manage/${id}/organizer.activity-edition.edition`
      )
      .toString();
  }

  public openShareModal(): void {
    if (!this.hasShareModal) {
      this.window.navigator.share({
        title: this.activity.name,
        url: this.activityUrl,
      });
    }

    this.shareModal.show();
  }

  public redirectAfterDeletion(): void {
    this.redirectionService.redirect('/organizer.activity.list');
  }

  public showConfirmActivityCancellationModal(): void {
    this.confirmActivityCancellationModal.show();
  }

  public get haveIncompleteFields(): boolean {
    const toggleVariant =
      ToggleNameEnum.OrganizerSharedGuardActivityByTypeMandatoryFields;
    const mandatoryFieldsVariant = this.toggleService.getVariant(
      toggleVariant,
      activityMandatoryFieldsDefaultValue
    ) as string[];

    return checkActivityMandatoryFields(this.activity, mandatoryFieldsVariant);
  }

  public goToActivityPlanning(): void {
    if (this.haveIncompleteFields) {
      this.haveIncompleteFieldsModal.show();
      return;
    }
    this.redirectionService.redirect(this.newDatesActionRouterLink, {
      queryParams: this.newDatesActionQueryParams,
    });
  }
}
